import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, imgUrl, rupee } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CheckCircle2, Truck, CreditCard } from "lucide-react";
import { toast } from "sonner";

const Checkout = () => {
  const { cart, cartSubtotal, clearCart, user } = useStore();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState("cod");
  const [addr, setAddr] = useState({
    full_name: user?.name || "", phone: user?.phone || "", email: user?.email || "",
    line1: "", line2: "", city: "", state: "Gujarat", pincode: "",
  });

  const shipping = cartSubtotal >= 999 ? 0 : 79;
  const total = cartSubtotal + shipping;
  const set = (k) => (e) => setAddr({ ...addr, [k]: e.target.value });

  if (cart.length === 0 && !order) return (
    <div className="py-32 text-center"><h1 className="font-serif text-3xl">Your cart is empty</h1><Button asChild className="mt-4 rounded-none bg-burgundy"><Link to="/new-arrivals">Shop Now</Link></Button></div>
  );

  const validAddr = addr.full_name && addr.phone && addr.line1 && addr.city && addr.state && addr.pincode;

  const placeOrder = async () => {
    if (payment === "online") { toast.error("Online payment gateway is being set up. Please choose Cash on Delivery for now."); return; }
    setLoading(true);
    try {
      const items = cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size, color: i.color }));
      const r = await api.post("/orders", { items, address: addr, payment_method: "cod", email: addr.email });
      setOrder(r.data); clearCart(); setStep(5); window.scrollTo(0, 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Order failed. Please try again."); }
    finally { setLoading(false); }
  };

  if (step === 5 && order) return (
    <div className="mx-auto max-w-lg px-6 py-20 text-center" data-testid="order-confirmation">
      <CheckCircle2 className="mx-auto h-16 w-16 text-green-600" />
      <h1 className="mt-4 font-serif text-3xl text-burgundy">Order Confirmed!</h1>
      <p className="mt-2 text-muted-text">Thank you for shopping with ANUSWA. Your order number is</p>
      <p className="mt-1 font-serif text-2xl text-charcoal" data-testid="order-number">{order.order_no}</p>
      <p className="mt-4 text-sm text-muted-text">Payment: Cash on Delivery · Total {rupee(order.total)}</p>
      <div className="mt-8 flex justify-center gap-3">
        <Button asChild variant="outline" className="rounded-none border-burgundy text-burgundy"><Link to="/account">View Orders</Link></Button>
        <Button asChild className="rounded-none bg-burgundy"><Link to="/">Continue Shopping</Link></Button>
      </div>
    </div>
  );

  const steps = ["Information", "Delivery", "Review", "Payment"];

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 md:px-6">
      <h1 className="font-serif text-4xl text-burgundy">Checkout</h1>
      <div className="mt-4 flex gap-2 text-xs">
        {steps.map((s, i) => <span key={s} className={`flex-1 border-b-2 pb-2 text-center ${step >= i + 1 ? "border-burgundy text-burgundy" : "border-border text-muted-text"}`}>{i + 1}. {s}</span>)}
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
        <div>
          {step === 1 && (
            <div className="space-y-4" data-testid="step-info">
              <h2 className="font-serif text-xl">Customer Information</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label>Full Name</Label><Input value={addr.full_name} onChange={set("full_name")} data-testid="co-name" className="mt-1 rounded-none" /></div>
                <div><Label>Mobile Number</Label><Input value={addr.phone} onChange={set("phone")} data-testid="co-phone" className="mt-1 rounded-none" /></div>
              </div>
              <div><Label>Email</Label><Input type="email" value={addr.email} onChange={set("email")} data-testid="co-email" className="mt-1 rounded-none" /></div>
              <Button onClick={() => (addr.full_name && addr.phone ? setStep(2) : toast.error("Enter name and phone"))} className="rounded-none bg-burgundy" data-testid="to-delivery">Continue to Delivery</Button>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4" data-testid="step-delivery">
              <h2 className="font-serif text-xl">Delivery Address</h2>
              <div><Label>Address Line 1</Label><Input value={addr.line1} onChange={set("line1")} data-testid="co-line1" className="mt-1 rounded-none" /></div>
              <div><Label>Address Line 2 (optional)</Label><Input value={addr.line2} onChange={set("line2")} data-testid="co-line2" className="mt-1 rounded-none" /></div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div><Label>City</Label><Input value={addr.city} onChange={set("city")} data-testid="co-city" className="mt-1 rounded-none" /></div>
                <div><Label>State</Label><Input value={addr.state} onChange={set("state")} data-testid="co-state" className="mt-1 rounded-none" /></div>
                <div><Label>Pincode</Label><Input value={addr.pincode} onChange={set("pincode")} data-testid="co-pincode" className="mt-1 rounded-none" /></div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="rounded-none">Back</Button>
                <Button onClick={() => (validAddr ? setStep(3) : toast.error("Please fill all address fields"))} className="rounded-none bg-burgundy" data-testid="to-review">Continue to Review</Button>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-4" data-testid="step-review">
              <h2 className="font-serif text-xl">Order Summary</h2>
              <div className="border border-border">
                {cart.map((i) => (
                  <div key={i.key} className="flex gap-3 border-b border-border p-3 last:border-0">
                    <img src={imgUrl(i.image)} alt={i.name} className="h-16 w-14 object-cover" />
                    <div className="flex-1 text-sm"><p className="font-medium">{i.name}</p><p className="text-xs text-muted-text">Qty {i.quantity} {i.size && `· ${i.size}`}</p></div>
                    <span className="text-sm font-medium">{rupee(i.price * i.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="border border-border p-4 text-sm">
                <p className="font-medium">{addr.full_name}, {addr.phone}</p>
                <p className="text-muted-text">{addr.line1}, {addr.line2} {addr.city}, {addr.state} - {addr.pincode}</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(2)} className="rounded-none">Back</Button>
                <Button onClick={() => setStep(4)} className="rounded-none bg-burgundy" data-testid="to-payment">Continue to Payment</Button>
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-4" data-testid="step-payment">
              <h2 className="font-serif text-xl">Payment Method</h2>
              <RadioGroup value={payment} onValueChange={setPayment} className="space-y-3">
                <label className="flex cursor-pointer items-center gap-3 border border-border p-4 has-[:checked]:border-burgundy" data-testid="pay-cod">
                  <RadioGroupItem value="cod" /> <Truck className="h-5 w-5 text-burgundy" />
                  <div><p className="font-medium">Cash on Delivery</p><p className="text-xs text-muted-text">Pay when your order arrives</p></div>
                </label>
                <label className="flex cursor-pointer items-center gap-3 border border-border p-4 opacity-60" data-testid="pay-online">
                  <RadioGroupItem value="online" /> <CreditCard className="h-5 w-5 text-burgundy" />
                  <div><p className="font-medium">Online Payment</p><p className="text-xs text-muted-text">Card / UPI — gateway setup in progress</p></div>
                </label>
              </RadioGroup>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(3)} className="rounded-none">Back</Button>
                <Button onClick={placeOrder} disabled={loading} className="rounded-none bg-burgundy" data-testid="place-order-btn">{loading ? "Placing..." : `Place Order · ${rupee(total)}`}</Button>
              </div>
            </div>
          )}
        </div>

        <div className="h-fit border border-border p-6">
          <h3 className="font-serif text-lg text-burgundy">Order Total</h3>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal ({cart.length} items)</span><span>{rupee(cartSubtotal)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "Free" : rupee(shipping)}</span></div>
            <div className="mt-2 flex justify-between border-t border-border pt-3 font-serif text-lg text-burgundy"><span>Total</span><span>{rupee(total)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
